/**
 * An exercise's progression as a MAP of `measurement field → rule`, and the reads a surface makes of
 * one. This is the draft's own vocabulary for the wire's `progressionRules` document;
 * `progressionWire.ts` is the single seam between the two.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionRules.ts`, 1:1 ──────
 * The semantics are the phone's, because the rule they describe is the BACKEND's and two clients
 * authoring the same document have to mean the same thing by it. Nothing here is web-specific.
 *
 * THE INVARIANT: the key is the field the rule WRITES, never the one it reads. A field with no entry
 * rides flat across the phase's cycles, so "no progression" is an absent key rather than a rule that
 * says nothing. Because a map key is unique, "one rule per field" costs no validation.
 *
 * THE TWO FAMILIES (plan-progression R3): `Static` expands the authored base across the phase's
 * cycles at authoring time, and `PreviousExecution` resolves at workout start off the client's
 * previous in-phase execution, gated on whether they DELIVERED it. Either may key any column — the
 * stepped field is free, so a duration band driving rounds is the same rule as reps driving load.
 *
 * Pricing a set as a fraction of the client's estimated max is NOT a family here: it is the UNIT the
 * load is written in (`prescriptionMode` plus the exercise's `realMaxMultiplier`), so it resolves
 * under both families and under none.
 *
 * ── This replaced an OPAQUE map, and the replacement is what the port is ─────────────────────
 * Between 15 August and today `ExerciseDraft.progressionRules` was carried byte-identical and never
 * opened, because reading a rule without an engine that agrees with the backend's would have been a
 * preview that lies. The engine is `progressionMatrix.ts` and the fixture that gates it is
 * `progressionParity.test.ts`; this module is what both of them read.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

/** The backend `ProgressionType` names — the discriminator the wire document carries verbatim. */
export type ProgressionRuleType = 'Static' | 'PreviousExecution';

/**
 * What a delta's number MEANS. `Absolute` adds it in the unit of the column the rule advances —
 * kilograms on the load, repetitions on a rep count, seconds on a duration, points on RIR — and is
 * the ordinary case on every column. `Percent` grows the reference compoundly, and the LOAD alone
 * accepts it; the backend refuses it elsewhere, so no surface offers it there.
 *
 * Every rule states one, flat rules included: a delta's meaning is never inferred from a missing key.
 */
export type ProgressionDeltaUnit = 'Absolute' | 'Percent';

/**
 * The document a rule ARRIVED as, held verbatim so the fields this build has no reading for come
 * back out on the next whole-tree save.
 *
 * `unreadable` in `progressionWire.ts` covers a whole document this build cannot open. This covers
 * the other half of the same promise: a document of a type it CAN open, carrying one more property
 * than it knows about. A newer backend adding an optional field to `PreviousExecutionRuleDocument`
 * would otherwise have it deleted by the first save from this client, silently, because every save
 * here is the whole tree.
 *
 * Absent on a rule the trainer has just authored, which has no document behind it yet.
 */
export type ProgressionRuleDocument = Readonly<Record<string, unknown>>;

/** Authoring-time expansion of the authored base across the phase's cycles. A null delta is flat. */
export interface StaticProgressionRule {
  readonly type: 'Static';
  readonly delta: number | null;
  readonly deltaUnit: ProgressionDeltaUnit;
  readonly wireDocument?: ProgressionRuleDocument;
}

/**
 * Resolved at workout START off the client's previous in-phase execution, gated on their having
 * DELIVERED it (R3): every counted set marked done, and every column that row was priced on reached.
 *
 * `banded` IS NOT THE TRIGGER. Delivering is asked of the WHOLE set — the backend judges every
 * prescribed column that decides progression — so a rule needs no column to fire. What `banded`
 * names is the one column carrying a CEILING above its ordinary prescription: the bar the client
 * climbs to before this rule jumps. `null` is therefore a complete rule, not a half-authored one:
 * "advance when the client delivers the whole set".
 *
 * It lives on the rule rather than beside it on the exercise because two places to write it would be
 * two places for them to disagree.
 *
 * THE DELTA IS NOT NULLABLE HERE, and that is the wire's shape rather than a preference.
 * `Static.Delta` is `decimal?` on the backend — a flat rule is a legal, meaningful configuration —
 * while `PreviousExecutionRuleDocument.Delta` is a plain `decimal`, so a document without one
 * deserialises to a zero step nobody authored. Stating it in the type is what turns "remember to
 * default the step" from discipline into a compile error at the one place it matters.
 */
export interface PreviousExecutionProgressionRule {
  readonly type: 'PreviousExecution';
  /**
   * The column whose ceiling the client climbs to, or null when the rule advances on the delivered
   * set alone. Never the key this rule sits under, never a field carrying a rule, never one that
   * does not decide progression.
   */
  readonly banded: MeasurementFieldKey | null;
  readonly delta: number;
  readonly deltaUnit: ProgressionDeltaUnit;
  readonly wireDocument?: ProgressionRuleDocument;
}

export type ProgressionRule = StaticProgressionRule | PreviousExecutionProgressionRule;

/**
 * One exercise's rules, keyed by the field each one writes. An absent key is a field that rides flat
 * — the ONE spelling of "nothing progresses here", so an empty map and a map of flat rules are never
 * both in play.
 */
export type ProgressionRules = Readonly<Partial<Record<MeasurementFieldKey, ProgressionRule>>>;

/** No rules at all — every column rides flat. What a freshly-picked exercise starts with. */
export const NO_PROGRESSION_RULES: ProgressionRules = {};

/** The rule writing one field, or null when that field rides flat. */
export function ruleFor(
  rules: ProgressionRules,
  field: MeasurementFieldKey | null,
): ProgressionRule | null {
  return field === null ? null : (rules[field] ?? null);
}

/**
 * Put a rule on one field, or take it off (`null`). **The ONE writer**: everything else in this app
 * reads. Writing leaves every other field's rule exactly where it was — which is what makes a
 * single-column editor safe over a document it cannot fully show.
 */
export function withRule(
  rules: ProgressionRules,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): ProgressionRules {
  const next: Partial<Record<MeasurementFieldKey, ProgressionRule>> = { ...rules };
  if (rule === null) delete next[field];
  else next[field] = rule;
  return next;
}

/**
 * Whether a rule ACTUALLY advances the column it sits on — the one reading of "is anything
 * progressing here", for every surface that asks (which column the sheet opens on, which family is
 * in force, what the card's foot row says).
 *
 * A `PreviousExecution` rule always does: it resolves at workout start off the client's own history,
 * so it carries a progression even before a delta is typed. A `Static` rule does so only WITH a
 * delta — without one it expands the authored base unchanged across the phase, which is the same
 * plan as no rule at all.
 *
 * That distinction is not cosmetic. The backend normalises both an omitted document and an
 * explicitly empty one to a single flat `Static` on WEIGHT (`ProgressionRuleSet.FlatWeightDefault`),
 * so after any save and reload every exercise carries a weight key — including movements that
 * measure no load and trainers who authored nothing. Read as a rule, that artefact answers "which
 * column carries the progression" with a column nobody chose. Read through here, it answers nothing,
 * which is what it is.
 */
export function carriesProgression(rule: ProgressionRule | null | undefined): boolean {
  if (rule === null || rule === undefined) return false;
  return rule.type === 'PreviousExecution' || rule.delta !== null;
}

/** The delta a rule advances by, or null when it carries none (a flat `Static`). */
export function ruleDelta(rule: ProgressionRule | null): number | null {
  return rule?.delta ?? null;
}

/** The delta's unit, or null when the rule carries none. */
export function ruleDeltaUnit(rule: ProgressionRule | null): ProgressionDeltaUnit | null {
  return rule?.deltaUnit ?? null;
}

/** Every field this exercise already has a rule on, in whatever order the map holds them. */
export function ruledFields(rules: ProgressionRules): MeasurementFieldKey[] {
  return Object.keys(rules) as MeasurementFieldKey[];
}
